import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import api from '../config/api';

interface User {
  id: string;
  name: string;
  email: string;
  phone?: string;
  wallet_balance?: number;
  role?: 'user' | 'admin' | 'super_admin';
}

interface AuthContextType {
  user: User | null;
  userToken: string | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  isAdmin: boolean;
  login: (email: string, password: string) => Promise<boolean>;
  register: (userData: any) => Promise<{ success: boolean; requiresVerification?: boolean; error?: string }>;
  logout: () => void;
  updateUser: (userData: Partial<User>) => void;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

interface AuthProviderProps {
  children: ReactNode;
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [userToken, setUserToken] = useState<string | null>(null);
  const [isAuthenticated, setIsAuthenticated] = useState<boolean>(false);
  const [isLoading, setIsLoading] = useState<boolean>(true);

  useEffect(() => {
    // Check for stored token on app start
    const storedToken = localStorage.getItem('userToken');
    const storedUser = localStorage.getItem('user');

    if (storedToken && storedUser) {
      setUserToken(storedToken);
      setUser(JSON.parse(storedUser));
      setIsAuthenticated(true);
    } else {
      setIsAuthenticated(false);
    }
    setIsLoading(false);
  }, []);

  // Mobil Uygulama Bildirim Token'ını Güncelle
  useEffect(() => {
    let isSubscribed = true;

    const sendTokenToServer = async (token: string, os?: string) => {
      if (isAuthenticated && token && token !== 'undefined' && isSubscribed) {
        try {
          const lastSentToken = localStorage.getItem('last_sent_fcm_token');
          const lastSentOS = localStorage.getItem('last_sent_device_os');
          
          // Sadece token veya OS değiştiyse gönder
          if (lastSentToken !== token || lastSentOS !== os) {
            await api.post('/user/update-fcm-token', { 
                fcm_token: token,
                device_os: os 
            });
            localStorage.setItem('last_sent_fcm_token', token);
            if (os) localStorage.setItem('last_sent_device_os', os);
            console.log('Mobile push token & OS updated successfully');
          }
        } catch (error) {
          // console.error('Failed to update mobile push token:', error);
        }
      }
    };

    // Sunucu tarafındaki badge'i sıfırla
    // @ts-ignore
    window.clearServerBadge = async () => {
      if (isAuthenticated && isSubscribed) {
        try {
          await api.post('/user/clear-badge');
          console.log('Server badge cleared');
        } catch (error) {
          // ignore
        }
      }
    };

    // Mobil uygulama token'ı enjekte ederse veya zaten varsa çalıştır
    // @ts-ignore
    window.onExpoPushTokenReady = (token: string, os: string) => {
      sendTokenToServer(token, os);
    };

    // @ts-ignore
    const currentToken = window.EXPO_PUSH_TOKEN;
    // @ts-ignore
    const currentOS = window.DEVICE_OS;
    
    if (currentToken) {
      sendTokenToServer(currentToken, currentOS);
    }

    return () => {
      isSubscribed = false;
      // @ts-ignore
      delete window.onExpoPushTokenReady;
      // @ts-ignore
      delete window.clearServerBadge;
    };
  }, [isAuthenticated]);

  const login = async (email: string, password: string): Promise<boolean> => {
    try {
      const response = await api.post('/login', { email, password });

      if (response.data.token) {
        setUserToken(response.data.token);
        setUser(response.data.user);
        setIsAuthenticated(true);

        localStorage.setItem('userToken', response.data.token);
        localStorage.setItem('user', JSON.stringify(response.data.user));

        return true;
      }
      return false;
    } catch (error) {
      console.error('Login error:', error);
      return false;
    }
  };

  const register = async (userData: any): Promise<{ success: boolean; requiresVerification?: boolean; error?: string }> => {
    try {
      // Frontend'teki confirmPassword alanını backend'in beklediği password_confirmation'a çevir
      const apiData = {
        name: userData.name,
        email: userData.email,
        phone: userData.phone,
        password: userData.password,
        password_confirmation: userData.confirmPassword,
        referral_code: userData.referral_code,
        utm_source: userData.utm_source,
        utm_medium: userData.utm_medium,
        utm_campaign: userData.utm_campaign,
        utm_term: userData.utm_term,
        utm_content: userData.utm_content,
        gclid: userData.gclid,
        referrer_url: userData.referrer_url,
      };

      const response = await api.post('/register', apiData);

      if (response.data.token) {
        setUserToken(response.data.token);
        setUser(response.data.user);
        setIsAuthenticated(true);

        localStorage.setItem('userToken', response.data.token);
        localStorage.setItem('user', JSON.stringify(response.data.user));

        return {
          success: true,
          requiresVerification: response.data.requires_verification
        };
      }
      return { success: false, error: 'Token alınamadı' };
    } catch (error: any) {
      console.error('Register error:', error);
      return {
        success: false,
        error: error.response?.data?.message || 'Kayıt işlemi başarısız'
      };
    }
  };

  const logout = () => {
    setUser(null);
    setUserToken(null);
    setIsAuthenticated(false);
    localStorage.removeItem('userToken');
    localStorage.removeItem('user');
  };

  const updateUser = (userData: Partial<User>) => {
    if (user) {
      const updatedUser = { ...user, ...userData };
      setUser(updatedUser);
      localStorage.setItem('user', JSON.stringify(updatedUser));
    }
  };

  const isAdmin = user?.role === 'admin' || user?.role === 'super_admin';

  const value: AuthContextType = {
    user,
    userToken,
    isAuthenticated,
    isLoading,
    isAdmin,
    login,
    register,
    logout,
    updateUser,
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};

export { AuthContext };
